import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServicesAPI } from "../api/services";
import type {
  Service,
  CreateServiceRequest,
  UpdateServiceRequest,
} from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { isDemoMode } from "@/features/utils/demo-mode";
import { mockServices } from "./use-services.mock";

// Query keys
export const servicesKeys = {
  all: ["services"] as const,
  lists: () => [...servicesKeys.all, "list"] as const,
  list: (clientId: string) => [...servicesKeys.lists(), clientId] as const,
  details: () => [...servicesKeys.all, "detail"] as const,
  detail: (clientId: string, serviceId: string) =>
    [...servicesKeys.details(), clientId, serviceId] as const,
};

// Query hooks
export function useServices() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Service[]>({
    queryKey: servicesKeys.list(clientId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(mockServices);
      }
      return ServicesAPI.listServices(clientId);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

export function useService(serviceId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Service>({
    queryKey: servicesKeys.detail(clientId, serviceId),
    queryFn: () => {
      if (isDemoMode()) {
        const service = mockServices.find((s) => s.id === serviceId);
        return Promise.resolve(service!);
      }
      return ServicesAPI.getService(clientId, serviceId);
    },
    enabled: !!clientId && !!serviceId,
    staleTime: 30 * 1000,
  });
}

// Mutation hooks
export function useCreateService() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceRequest) =>
      ServicesAPI.createService(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: servicesKeys.list(clientId),
      });
    },
  });
}

export function useUpdateService() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      data,
    }: {
      serviceId: string;
      data: UpdateServiceRequest;
    }) => ServicesAPI.updateService(clientId, serviceId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: servicesKeys.list(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: servicesKeys.detail(clientId, variables.serviceId),
      });
    },
  });
}

export function useDeleteService() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (serviceId: string) =>
      ServicesAPI.deleteService(clientId, serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: servicesKeys.list(clientId),
      });
    },
  });
}
